import React, { useState, useEffect } from 'react';
import EligibleSupportersList from '../components/EligibleSupportersList';
import SupportButton from '../components/SupportButton';

const SponsorshipShowPage = ({ sponsorshipId, currentUserId }) => {
  const [sponsorship, setSponsorship] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSponsorship();
  }, [sponsorshipId]);

  const fetchSponsorship = async () => {
    try {
      const response = await fetch(`/api/v1/sponsorships/${sponsorshipId}`, {
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSponsorship(data.data.sponsorship);
      } else {
        setError(data.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'En attente de soutiens', bg: '#fff3cd', color: '#8a5b00' },
      approved: { text: 'Approuvé', bg: '#dcfce7', color: '#166534' },
      completed: { text: 'Complété', bg: '#f3e8ff', color: '#7c3aed' },
      rejected: { text: 'Rejeté', bg: '#fee2e2', color: '#b91c1c' }
    };

    const badge = badges[status] || { text: status, bg: '#f3f4f6', color: '#4b5563' };
    
    return (
      <span style={{ background: badge.bg, color: badge.color, padding: '7px 12px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700 }}>
        {badge.text}
      </span>
    );
  };

  const getLevelLabel = (level) => {
    const labels = {
      sympathisant: 'Adhérent Sympathisant',
      participant: 'Adhérent Participant',
      actif: 'Adhérent Actif'
    };
    return labels[level] || level;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isInitiator = sponsorship?.initiator?.id === currentUserId;
  const isAlreadySupporter = sponsorship?.supporters?.some(s => s.supporter_id === currentUserId);
  const canSupport = !isInitiator && !isAlreadySupporter && sponsorship?.status === 'pending';

  if (loading) {
    return (
      <div style={{ background: '#f7eef2', minHeight: '100vh', paddingTop: '120px' }}>
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!sponsorship) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning" role="alert">
          Demande de parrainage non trouvée.
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f7eef2', minHeight: '100vh', paddingTop: '88px', paddingBottom: '80px' }}>
      <div className="container" style={{ maxWidth: 1120 }}>
        <div style={{ background: 'linear-gradient(135deg,#7c2837,#a8384e)', borderRadius: 24, padding: '34px 38px', color: '#fff', marginBottom: 24, boxShadow: '0 24px 60px rgba(124,40,55,0.18)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.14)', borderRadius: 999, padding: '8px 14px', fontSize: '0.8rem', fontWeight: 700, marginBottom: 14 }}>
                Parrainage
              </div>
              <h1 style={{ fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 800, marginBottom: 8, letterSpacing: '-0.03em' }}>Suivi de la demande de parrainage</h1>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.92)', lineHeight: 1.75, maxWidth: 760 }}>
                Consulte l'avancement, les soutiens deja recueillis et les prochaines actions necessaires pour faire aboutir la demande.
              </p>
            </div>
            {getStatusBadge(sponsorship.status)}
          </div>
        </div>

      <div className="row g-4">
        {/* Colonne principale */}
        <div className="col-lg-8">
          {/* Détails de la demande */}
          <div style={{ background: '#fff', borderRadius: 24, border: '1px solid rgba(124,40,55,0.12)', boxShadow: '0 18px 44px rgba(124,40,55,0.08)', padding: '30px 32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 22, flexWrap: 'wrap' }}>
              <h3 className="mb-0" style={{ color: '#2c0e16', fontWeight: 800 }}>Demande de parrainage</h3>
              {getStatusBadge(sponsorship.status)}
            </div>
            <div>
              <dl className="row">
                <dt className="col-sm-3">Email du Filleul:</dt>
                <dd className="col-sm-9">{sponsorship.filleul_email}</dd>

                <dt className="col-sm-3">Niveau Demandé:</dt>
                <dd className="col-sm-9">{getLevelLabel(sponsorship.requested_level)}</dd>

                <dt className="col-sm-3">Initiateur:</dt>
                <dd className="col-sm-9">{sponsorship.initiator?.name}</dd>

                <dt className="col-sm-3">Statut:</dt>
                <dd className="col-sm-9">{getStatusBadge(sponsorship.status)}</dd>

                <dt className="col-sm-3">Progression:</dt>
                <dd className="col-sm-9">
                  <div className="progress mb-2">
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ 
                        width: `${(sponsorship.supporters_count / sponsorship.required_supporters_count) * 100}%`,
                        background: 'linear-gradient(135deg,#7c2837,#a8384e)'
                      }}
                    >
                      {sponsorship.supporters_count} / {sponsorship.required_supporters_count}
                    </div>
                  </div>
                  <small className="text-muted">
                    {sponsorship.quorum_description}
                  </small>
                </dd>
              </dl>
            </div>
          </div>

          {/* Parrains de Soutien */}
          {sponsorship.supporters && sponsorship.supporters.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 24, border: '1px solid rgba(124,40,55,0.12)', boxShadow: '0 18px 44px rgba(124,40,55,0.08)', padding: '24px 26px', marginTop: 24 }}>
                <h5 className="mb-3" style={{ color: '#2c0e16', fontWeight: 800 }}>
                  Parrains de Soutien ({sponsorship.supporters.length})
                </h5>
                <ul className="list-group list-group-flush">
                  {sponsorship.supporters.map(supporter => (
                    <li 
                      key={supporter.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                      style={{ borderColor: 'rgba(124,40,55,0.08)', paddingLeft: 0, paddingRight: 0 }}
                    >
                      <div>
                        <strong>{supporter.supporter_name}</strong>
                        <br />
                        <small className="text-muted">
                          Soutien accepté le {formatDate(supporter.responsibilities_accepted_at)}
                        </small>
                      </div>
                      <span style={{ background: '#dcfce7', color: '#166534', padding: '6px 10px', borderRadius: 999, fontWeight: 700 }}>✓</span>
                    </li>
                  ))}
                </ul>
            </div>
          )}

          {/* Inviter des Parrains (si initiateur) */}
          {isInitiator && sponsorship.status === 'pending' && (
            <div className="mt-4">
              <EligibleSupportersList 
                sponsorshipId={sponsorshipId}
                onInvite={() => fetchSponsorship()}
              />
            </div>
          )}

          {/* Soutenir cette Demande (si peut soutenir) */}
          {canSupport && (
            <div className="mt-4">
              <h4 style={{ color: '#00256C' }}>Soutenir cette Demande</h4>
              <SupportButton 
                sponsorshipId={sponsorshipId}
                onSupport={() => fetchSponsorship()}
              />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="col-lg-4">
          <div style={{ background: '#fff', borderRadius: 24, border: '1px solid rgba(124,40,55,0.12)', boxShadow: '0 18px 44px rgba(124,40,55,0.08)', padding: '24px 26px' }}>
              <h6 className="mb-3" style={{ color: '#2c0e16', fontWeight: 800 }}>
                Informations
              </h6>
              <p><strong>Date de création:</strong></p>
              <p className="text-muted">{formatDate(sponsorship.created_at)}</p>

              {sponsorship.status === 'pending' && (
                <div className="alert" style={{ background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.12)', color: '#7a4050', borderRadius: 16 }}>
                  <small>
                    Cette demande est en attente de soutiens. 
                    {sponsorship.missing_supporters_count > 0 && (
                      ` Il manque encore ${sponsorship.missing_supporters_count} soutien(s).`
                    )}
                  </small>
                </div>
              )}

              {sponsorship.status === 'approved' && (
                <div className="alert" style={{ background: '#dcfce7', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 16 }}>
                  <small>
                    Cette demande a été approuvée ! Une invitation sera envoyée au filleul.
                  </small>
                </div>
              )}

              {sponsorship.status === 'completed' && (
                <div className="alert" style={{ background: '#f3e8ff', border: '1px solid #e9d5ff', color: '#6b21a8', borderRadius: 16 }}>
                  <small>
                    Le filleul a rejoint l'association avec succès !
                  </small>
                </div>
              )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default SponsorshipShowPage;